import { BaseComponent } from '../base.component';
import { Router, ActivatedRoute, Params } from '@angular/router';
import { Component, OnInit } from '@angular/core';
import { UUID } from 'angular2-uuid';
import { NgbDateStruct, NgbTimeStruct } from '@ng-bootstrap/ng-bootstrap';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Alert, CustomProffixService, HelpersService, UserSessionService, StundenAntrag } from '../../services';

import {
  Login,
  Mitarbeiter,
  MitarbeiterService,
  StundenService,
  Stunden,
  StundenartService,
  Stundenart
} from '@proffix/restapi-angular-library';

@Component({
  selector: 'app-requests-new',
  templateUrl: './requests-new.component.html',
  styleUrls: ['./requests-new.component.css']
})
export class RequestsNewComponent extends BaseComponent implements OnInit {
  static readonly DEFAULT_STUNDENART = 'STD';

  proffixLogin: Login = null;

  stundenAntrag: StundenAntrag;
  stundenAntragDatum: NgbDateStruct;
  stundenAntragStartZeit: NgbTimeStruct;
  stundenAntragEndZeit: NgbTimeStruct;
  stundenAntragStundenart: Stundenart;

  listOfStundenarten: Stundenart[];

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _customProffixService: CustomProffixService,
    private _helpersService: HelpersService,
    private _userSessionService: UserSessionService,
    private _mitarbeiterService: MitarbeiterService,
    private _stundenService: StundenService,
    private _stundenartService: StundenartService,
    private _modalService: NgbModal,
  ) {
    super();
    this.stundenAntrag = {
      Stunden: 0,
      Pause: 0,
    };
    this.stundenAntragDatum = this.getNgbDateStruct(new Date());
    this.stundenAntragStartZeit = { hour: 0, minute: 0, second: 0 };
    this.stundenAntragEndZeit = { hour: 0, minute: 0, second: 0 };
  }

  ngOnInit() {
    // load current PROFFIX user
    this._userSessionService.getProffixLogin().subscribe(data => {
      this.proffixLogin = data;

      // load the "Stundenarten"
      this._stundenartService.getAll({}).subscribe(stundenarten => {
        stundenarten.sort((x, y) => {
          if (x.Bezeichnung < y.Bezeichnung) {
            return -1;
          } else if (x.Bezeichnung > y.Bezeichnung) {
            return 1;
          } else {
            return 0;
          }
        });

        this.listOfStundenarten = stundenarten;


        // Subscribe for a parameter "StundenNr" to make this a change request
        this._route.params.subscribe(params => {
          const stundenNr = +params['stundenNr'];

          if (stundenNr) {
            // load the original "Stunden" record
            this._stundenService.get(stundenNr).subscribe((original) => {
              this.stundenAntrag.OriginalStunden = original;

              // assign the original values to the change request
              this.stundenAntragDatum = this.getNgbDateStruct(original.Datum);
              this.stundenAntragStartZeit = this.getNgbTimeStruct(original.StartZeit);
              if (original.EndZeit) {
                this.stundenAntragEndZeit = this.getNgbTimeStruct(original.EndZeit);
              } else {
                this.stundenAntragEndZeit = this.getNgbTimeStruct(original.StartZeit);
              }
              this.stundenAntrag.Pause = original.Pause;
              this.stundenAntrag.Stunden = original.Stunden;

              // try to assign the "Stundenart" from the original "Stunden" record as the selected option
              const originalStundenart = this.listOfStundenarten.find(x => x.StundenartNr === original.Stundenart.StundenartNr);
              if (originalStundenart) { this.stundenAntragStundenart = originalStundenart; }
            });
          } else {
            // try to assign the default "Stundenart" as the selected option
            const defaultStundenart = this.listOfStundenarten.find(x => x.StundenartNr === RequestsNewComponent.DEFAULT_STUNDENART);
            if (defaultStundenart) { this.stundenAntragStundenart = defaultStundenart; }
          }
        });

      });
    });
  }

  public onTimeChanged(): void {
    let workedHours = 0;

    if (this.stundenAntragEndZeit.hour >= this.stundenAntragStartZeit.hour) {
      workedHours = this._helpersService.calculateTimeSpanFromNumbers(
        this.stundenAntragStartZeit.hour,
        this.stundenAntragStartZeit.minute,
        this.stundenAntragEndZeit.hour,
        this.stundenAntragEndZeit.minute
      );
    }

    this.stundenAntrag.Stunden = workedHours - this.stundenAntrag.Pause;
  }

  onStundenAntragStartZeitChanged(time: NgbTimeStruct): void {
    this.stundenAntragStartZeit = time;
    this.onTimeChanged();
  }

  onStundenAntragEndZeitChanged(time: NgbTimeStruct): void {
    this.stundenAntragEndZeit = time;
    this.onTimeChanged();
  }

  public onSubmit(content: any): void {
    if(this.validate()) {
      this.prepareStundenantragForSubmit();

      this._customProffixService.postStundenAntrag(this.stundenAntrag).subscribe(() => {
        this._modalService.open(content).result.then(
          () => { this._router.navigate(['requests']); },
          () => { this._router.navigate(['requests']); }
        );
      });
    }
  }

  private validate(): boolean {
    let isValid = true;

    if (!this.stundenAntrag.OriginalStunden && this.stundenAntrag.Stunden === 0) {
      // permit saving requests with an amount of 0.0 hours
      this.addWarningAlert('Die Ist-Stunden dürfen bei neuen Anträgen nicht 0.0 sein.');

      isValid = false;
    }
    if (!this.stundenAntrag.BemerkungMitarbeiter) {
      // employees must comment their requests
      this.addWarningAlert('Es muss eine Bemerkung ausgefüllt werden.');

      isValid = false;
    }

    return isValid;
  }

  public filterVerwendeteStundenarten(s: Stundenart): boolean {
    return s.Zusatzfelder.Z_In_Zeitmanagement_WebApp_verwenden === true;
  }

  public onDeleteRequest(content: any): void {
    this.prepareStundenantragForSubmit();
    this.stundenAntrag.AntragsArt = 20; // delete request

    this._customProffixService.postStundenAntrag(this.stundenAntrag).subscribe(() => {
      this._modalService.open(content).result.then(
        () => { this._router.navigate(['requests']); },
        () => { this._router.navigate(['requests']); }
      );
    });
  }

  private prepareStundenantragForSubmit(): void {
    this.stundenAntrag.Id = UUID.UUID();
    this.stundenAntrag.StatusCode = 1; // pendent
    this.stundenAntrag.StundenartZEI = this.stundenAntragStundenart.StundenartNr;
    this.stundenAntrag.Datum = new Date(Date.UTC(this.stundenAntragDatum.year, this.stundenAntragDatum.month - 1, this.stundenAntragDatum.day));

    if (this.stundenAntragStundenart.ZeitErfassen === true) {
      // from/to hours are specified
      this.stundenAntrag.StartZeit = this.getTimeFromNgbTimeStruct(this.stundenAntragStartZeit);
      this.stundenAntrag.EndZeit = this.getTimeFromNgbTimeStruct(this.stundenAntragEndZeit);
    }

    if (this.stundenAntrag.OriginalStunden) {
      // This is a change request:
      // Copy the original information from the "Stunden" record
      this.stundenAntrag.MitNrPRO = this.stundenAntrag.OriginalStunden.Mitarbeiter.MitarbeiterNr;
      this.stundenAntrag.StundenNr = this.stundenAntrag.OriginalStunden.StundenNr;
      this.stundenAntrag.AntragsArt = 10; // change request
    } else {
      // This is a request for adding new hours:
      // Take the current employee
      this.stundenAntrag.MitNrPRO = this.proffixLogin.Mitarbeiter.MitarbeiterNr;

      if (this.stundenAntragStundenart.ZeitErfassen === true) {
        this.stundenAntrag.AntragsArt = 101; // new working hours
      } else {
        this.stundenAntrag.AntragsArt = 102; // new absence hours
      }
    }
  }

  private getNgbDateStruct(date: Date): NgbDateStruct {
    date = new Date(date);
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate()
    };
  }

  private getDateFromNgbDateStruct(ngbDateStruct: NgbDateStruct): Date {
    return new Date(Date.UTC(
      this.stundenAntragDatum.year,
      this.stundenAntragDatum.month - 1,
      this.stundenAntragDatum.day));
  }

  private getNgbTimeStruct(time: string): NgbTimeStruct {
    if (time) {
      return {
        hour: this._helpersService.getHour(time),
        minute: this._helpersService.getMinute(time),
        second: 0
      };
    } else {
      return { hour: 0, minute: 0, second: 0 };
    }
  }

  private getTimeFromNgbTimeStruct(ngbTimeStruct: NgbTimeStruct): string {
    let time = '';

    if (ngbTimeStruct.hour < 10) { time += '0'; }
    time += ngbTimeStruct.hour;
    time += ':';
    if (ngbTimeStruct.minute < 10) { time += '0'; }
    time += ngbTimeStruct.minute;

    return time;
  }
}



// WEBPACK FOOTER //
// C:/.NET Projects/Angular Apps/TimeManagement/src/app/dialogs/requests-new/requests-new.component.ts